import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

const ContestButton = ({
  settings,
  contestId,
  contestName,
  contestImage,
  backgroundImage,
  start_date,
  submissions_end_date,
  end_date,
  dropShadow = 'dark'
}) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));

  // Refs for measurement
  const containerRef = useRef(null); // Ref for the container div
  const textRef = useRef(null);      // Ref for the contestName div

  // --- Font Size Adjustment Logic ---
  useEffect(() => {
    const adjustFontSize = () => {
      const container = containerRef.current;
      const textElement = textRef.current;

      if (!container || !textElement) return; // Exit if refs are not ready

      // Reset styles temporarily for accurate measurement
      textElement.style.fontSize = ''; // Use default size from CSS (1rem)
      textElement.style.whiteSpace = 'nowrap'; // Prevent wrapping during measurement
      textElement.style.display = 'inline-block'; // Ensure scrollWidth measures content

      const containerWidth = container.clientWidth; // Width of the container
      const textWidth = textElement.scrollWidth;    // How wide the text *wants* to be
      const originalFontSize = parseFloat(getComputedStyle(textElement).fontSize); // Get base font size in px
      const minFontSize = 10; // Minimum font size in pixels (adjust as needed)

      // Restore display style after measurement
       textElement.style.display = '';

      if (textWidth > containerWidth) {
        // Text overflows, calculate new font size
        const scaleFactor = containerWidth / textWidth;
        const newSize = Math.max(originalFontSize * scaleFactor, minFontSize); // Apply scaling, respecting minimum size
        textElement.style.fontSize = `${newSize}px`;
      } else {
        // Text fits, ensure default size is used (or remove inline style)
        textElement.style.fontSize = ''; // Use the default 1rem
      }

      // Ensure it stays on one line and handles potential minor overflow
      textElement.style.whiteSpace = 'nowrap';
      textElement.style.overflow = 'hidden';
      textElement.style.textOverflow = 'ellipsis';
      textElement.style.display = 'block'; // Ensure it takes full available width for ellipsis calculation
      textElement.style.width = '100%'; // Required for ellipsis to work correctly

    };

    // Run adjustment initially and on window resize
    adjustFontSize();
    window.addEventListener('resize', adjustFontSize);

    // Cleanup listener on component unmount or dependency change
    return () => window.removeEventListener('resize', adjustFontSize);

  }, [contestId]); // Rerun effect if contestName changes

  // Update currentTime every second for real-time progress updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (settings?.language === "ru") {
    return null;
  }

  if (currentTime < start_date) {
    return null;
  }

  if (currentTime > parseInt(end_date) + 86400) {
    return null;
  }

  const buttonStyle = {
    backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url('${backgroundImage}')`
  };

  let phase = "";
  let progressPercentage = 0;

  if (currentTime < start_date) {
    phase = "submissions";
    progressPercentage = 0;
  } else if (currentTime < submissions_end_date) {
    phase = "submissions";
    progressPercentage = ((currentTime - start_date) / (submissions_end_date - start_date)) * 100;
  } else if (currentTime < end_date) {
    phase = "voting";
    progressPercentage = 100 - ((end_date - currentTime) / (end_date - submissions_end_date)) * 100;
  } else {
    phase = "ended";
    progressPercentage = 100;
  }

  // // Determine progress bar color based on remaining time in the current phase
  // let progressColor = "#228b14"; // default green
  // if (phase !== "ended") {
  //   const phaseEnd = phase === "submissions" ? submissions_end_date : end_date;
  //   const timeRemaining = phaseEnd - currentTime;
  //   if (timeRemaining <= 3600) {
  //     progressColor = "#ff0000"; // red if one hour or less is left
  //   } else if (timeRemaining <= 86400) {
  //     progressColor = "#979700"; // yellow if one day or less is left
  //   }
  // }




  // Helper functions for color interpolation
  const hexToRgb = (hex) => {
    let sanitizedHex = hex.replace('#', '');
    if (sanitizedHex.length === 3) {
      sanitizedHex = sanitizedHex.split('').map(c => c + c).join('');
    }
    const bigint = parseInt(sanitizedHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
  };

  const rgbToHex = (r, g, b) => {
    return "#" + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  const interpolateColor = (color1, color2, factor) => {
    const c1 = hexToRgb(color1);
    const c2 = hexToRgb(color2);
    const r = Math.round(c1.r + factor * (c2.r - c1.r));
    const g = Math.round(c1.g + factor * (c2.g - c1.g));
    const b = Math.round(c1.b + factor * (c2.b - c1.b));
    return rgbToHex(r, g, b);
  };

  // Determine progress bar color based on remaining time in the current phase
  let progressColor = "#228b14"; // default green

  if (phase === "ended") {
    progressColor = "#ff0000";
  } else {
    // Determine the end time for the current phase
    const phaseEnd = phase === "submissions" ? submissions_end_date : end_date;
    const timeRemaining = phaseEnd - currentTime;

    // Define thresholds (in seconds)
    const twoDays = 172800; // 48 hours
    const oneDay = 86400;   // 24 hours
    const threeHours = 10800; // 3 hours

    if (timeRemaining > twoDays) {
      // More than 48 hours: fully green
      progressColor = "#228b14";
    } else if (timeRemaining > oneDay) {
      // Between 48 and 24 hours: transition from green to yellow
      // At 172800 sec -> green, at 86400 sec -> yellow
      const factor = (twoDays - timeRemaining) / (twoDays - oneDay);
      progressColor = interpolateColor("#228b14", "#979700", factor);
    } else if (timeRemaining > threeHours) {
      // Between 24 and 3 hours: transition from yellow to red
      // At 86400 sec -> yellow, at 10800 sec -> red
      const factor = (timeRemaining - threeHours) / (oneDay - threeHours);
      progressColor = interpolateColor("#ff0000", "#979700", factor);
    } else {
      // Less than or equal to 3 hours: red
      progressColor = "#ff0000";
    }
  }





  return (
    <Link
      onClick={() => setShowSidebar(false)}
      to={`../contests/${contestId}/`}
      className="btn btn-outline-success w-100 filter pinned-contest mb-2 d-flex flex-column p-2"
      style={buttonStyle}
    >
      {/* Top row: Horizontal container with image and description */}
      <div className="d-flex align-items-center w-100">
        <div className="mr-3 ">
          <img
            src={contestImage}
            style={{
              height: "70px",
              filter: dropShadow === 'light'
                        ? 'drop-shadow(rgba(255, 255, 255, 0.3) 1px 1px 5px)'
                        : 'drop-shadow(2px 4px 6px black)',
              marginLeft: "-11px",
              marginTop: "-21px",
              marginBottom: "-10px"
            }}
            alt="Contest Icon"
          />
        </div>

        
        <div ref={containerRef} className="w-100" style={{ marginTop: "-3px", fontSize: "1rem", overflow: "hidden" }}>
          <div>
            <strong>
              {phase === "voting"
                ? "Voting Phase"
                : phase === "ended"
                ? "Contest Ended"
                : "Weekly Contest"}
            </strong>
          </div>
          {/* Contest Name text - ADD REF HERE */}
          {/* Add initial styles for no-wrap and overflow handling */}
          <div
            ref={textRef}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%' // Ensure it occupies available space for ellipsis
             }}
          >
            {contestName}
          </div>
        </div>
      </div>
      
      {/* Bottom row: Full width progress bar, only show if contest hasn't ended */}
      {phase !== "ended" && (
        <div className="mt-2 w-100">
          <div
            style={{
              background: "#c9c9c9",
              borderRadius: "15px",
              height: "0.20rem",
              width: "100%",
              overflow: "hidden"
            }}
          >
            <div style={{ background: progressColor, width: `${progressPercentage}%`, height: "100%" }}></div>
          </div>
        </div>
      )}
    </Link>
  );
};

export default ContestButton;
